import * as Sentry from '@sentry/browser';

// CONCEPT: browser-side error reporting. Before this, the frontend had NO error capture at all -
// no window.onerror, no unhandledrejection handler - so every exception across ~35k lines of
// src/ was invisible, including the ones users report as "the button does nothing".
// USE: importing it from main.js installs everything; nothing to call for ordinary code. To
// report deliberately: `import { captureError } from './core/sentry.js'` then captureError(e).
//
// Imported FIRST in main.js, above jquery. ES module imports execute in order, so anything
// imported above this would throw during its own module init with no handler installed - which
// is exactly the failure that is hardest to reproduce from a bug report.

// The DSN comes from the PAGE, not from here. PaceSentry::dsn() resolves the per-server
// sentry_dsn override once and the layouts publish the result, so muting a host with an empty
// value silences BOTH halves and redirecting sends both to the same project. This module used to
// hold a compiled constant, which meant a supposedly muted host kept reporting from the browser -
// a review caught that.
//
// There is deliberately NO fallback to a compiled default. A page that publishes no dsn reports
// nothing, which loses events if a future layout forgets the field - but the alternative defeats
// muting on that page, and a mute is an operational control that has to hold.
function pageCtx() {
    return window.__PAGE_CTX__ || {};
}

// Third-party noise this codebase cannot fix and does not want paged about. Browser extensions
// and injected scripts throw into our global handler with our DSN attached; left in, they are
// the majority of events on any public site and they bury the real faults.
const DENY_URLS = [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
];

const IGNORE_ERRORS = [
    // Fired by a browser when an <img> or fetch is blocked by an extension or a network filter.
    // Carries no stack and no actionable information.
    'Non-Error promise rejection captured',
    // ResizeObserver's benign notification, thrown by Chrome during normal layout. Bootstrap
    // and DataTables both trigger it; it is not a fault.
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
];

export function initSentry() {
    const ctx = pageCtx();

    // The layouts publish ENVIRONMENT. Only report from production: a dev machine running
    // `npm run dev` would otherwise fill the project with errors nobody is going to triage,
    // and HMR itself throws during normal work.
    if (ctx.environment !== 'production') {
        return;
    }

    // An empty or absent dsn means this host is muted, or the page did not publish one. Either
    // way there is nothing to report to.
    if (typeof ctx.sentryDsn !== 'string' || ctx.sentryDsn === '') {
        return;
    }

    // Every pattern here is a plain regex literal now - no lookbehind, no runtime construction -
    // so there is nothing left that can fail to build. The allowlist does not depend on a regex
    // succeeding: a rule that somehow did not apply would leave a field UNREDACTED, never
    // UNDROPPED, which is the failure mode worth designing for.

    Sentry.init({
        dsn: ctx.sentryDsn,
        environment: ctx.environment,
        // Empty string rather than appVersion when the deploy has not set it - see the comment
        // in the layouts. An honest absence groups better than a value that is always '0.0.1'.
        release: ctx.sentryRelease || undefined,

        // Errors only. No tracing, no session replay, no profiling: those are priced per event
        // and the question this is being installed to answer - "what is breaking in production
        // that nobody has reported?" - is answered by exceptions alone. Turning replay on later
        // is a two-line change and a separate cost conversation.
        tracesSampleRate: 0,

        // OFF, matching send_default_pii on the PHP side. This is what stops the SDK attaching
        // the URL's query string, cookies and form values - and this platform puts ?au=<UserID>
        // in storefront URLs and posts a login form through the same pages.
        sendDefaultPii: false,

        denyUrls: DENY_URLS,
        ignoreErrors: IGNORE_ERRORS,

        // Breadcrumbs are the reason a frontend error is diagnosable at all - they carry the
        // clicks and AJAX calls leading up to it - but the default set records request bodies
        // and full URLs. Scrubbed in beforeBreadcrumb below rather than switched off.
        maxBreadcrumbs: 25,
        beforeBreadcrumb: scrubBreadcrumb,
        beforeSend: scrubEvent,
    });

    // RowID only, to match PaceSentry::enrich(). No email, no name, no login id.
    // '0' is explicitly excluded: user() is never empty and Model_user::$RowID defaults to 0,
    // so an anonymous visitor arrives as a real-looking id that would group every logged-out
    // session in the platform under one user.
    if (ctx.userId && ctx.userId !== '0') {
        Sentry.setUser({ id: String(ctx.userId) });
    }

    // member_code, the same tag the PHP side sets, so a per-site fault can be told from a
    // platform-wide one. That question is unanswerable today and is most of the point.
    if (ctx.site) {
        Sentry.setTag('site', String(ctx.site));
    }

    Sentry.setTag('area', ctx.isCP ? 'control_panel' : 'storefront');
}

/**
 * The event's OWN request data, which is separate from its breadcrumbs and was the hole in the
 * first version of this file. httpContextIntegration.preprocessEvent() sets event.request from
 * getHttpRequestData(), so every event carries:
 *
 *   request.url              - window.location.href, so ?au=<UserID> in full
 *   request.headers.Referer  - the PREVIOUS page's href, so a page reached by clicking a link on
 *                              an ?au= page leaks the same id even when its own URL is clean
 *
 * Scrubbing breadcrumbs but not these would have left the impersonated customer's id on every
 * production event raised during a staff session.
 */

/**
 * ONE recursive pass over the whole event. beforeSend receives a plain object, so unlike the PHP
 * half - where Event is a typed object and field-by-field is forced by the API - this side can
 * scrub everything and does.
 *
 * Safe to apply blanket because both rules are conservative on non-secrets: the key rule only
 * fires on a credential-shaped NAME, and the string rules only fire on a credential-shaped
 * SHAPE. 'TypeError' and '/catalog/browse' pass through untouched, which the tests assert.
 */
// ---------------------------------------------------------------------------------------------
// ALLOWLIST, not a scrubber.
//
// This file used to redact by NAME - a vocabulary of credential and personal-data field names,
// applied across seven serialization shapes. Nine review rounds later it had oscillated four
// times between leaking and over-redacting, and the tenth found that this half and the PHP half -
// hand-mirrored - had silently diverged on 'PrimaryPhoneNumber': PHP redacted it, this did not.
// Every individual fix was correct; the design was not. A name vocabulary is an open set, and
// being wrong in either direction is a defect.
//
// So nothing is matched by name any more:
//
//   STRUCTURE is allowlisted. buildAllowedEvent() constructs a NEW object and copies over only
//   the fields named below. A field nobody thought of is dropped, not inspected - the one
//   property a scrubber can never have. event.extra, arbitrary contexts, request headers and
//   bodies, and breadcrumb metadata are gone entirely; that is where the leaks lived.
//
//   CONTENT is redacted by SHAPE. The strings that survive go through redactByShape(), which
//   masks what LOOKS like an email address, a token or a long digit run, and strips query strings
//   WHOLE rather than by parameter name. Shape has no vocabulary to be incomplete, and cannot
//   over-redact a field name because it never reads one.
//
// Kept deliberately in step with PaceSentry - same rules, same order - but there is far less to
// keep in step than there was, which is most of the point.
// ---------------------------------------------------------------------------------------------

const REDACTED = '***REDACTED***';

// Contexts the SDK sets that describe the ENVIRONMENT rather than the user or the page.
// 'pace' is this module's own channel, written by captureError(). It is allowlisted rather
// than dropped because a caller passing a context that silently vanishes is a worse API than
// one that does not accept context at all - and its values still go through shape redaction.
const ALLOWED_CONTEXTS = ['os', 'runtime', 'browser', 'trace', 'pace'];

/**
 * SHAPE-based redaction. No field names, no vocabulary - it masks values that LOOK sensitive
 * wherever they appear, which is why it has neither failure mode the name-based version had: it
 * cannot be incomplete about a name it never heard of, and it cannot blank 'capacity' because it
 * never reads a key.
 *
 * Ordered longest-match-first so a token inside a URL is masked by the URL rule rather than
 * half-masked by the token rule.
 */
export function redactByShape(text) {
    if (typeof text !== 'string' || text === '') {
        return text;
    }

    const rules = [
        // A URL with its query string and fragment removed. Not filtered by parameter name:
        // ?au=<UserID> is the impersonation parameter, and dropping the whole query is what
        // removes the need for a list of parameters to drop.
        [/(https?:\/\/[^\s"'<>]*?|\/[^\s"'<>?#]*)[?#][^\s"'<>]*/gi, '$1?' + REDACTED],
        // An email address - the one PII shape unambiguous from its form alone.
        [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, REDACTED],
        // An auth header value, including the scheme word.
        [/\b(Bearer|Basic|Token)\s+[^\s,;"']+/gi, '$1 ' + REDACTED],
        // A long opaque token: 24+ characters that CONTAIN A DIGIT and no slash. Both qualifiers
        // were added after a test caught the first version blanking file paths -
        // 'app/Controllers/ControlPanel/Order_settings' is 42 characters of token-shaped text,
        // and a stack trace without paths is not worth sending. Excluding '/' breaks a path into
        // short segments; requiring a digit spares long snake_case identifiers.
        //
        // Residual limit, stated rather than papered over: an all-alphabetic secret of this
        // length is not caught by shape. The Bearer/Basic rule above covers the case that
        // actually occurs, and every real key here (hex, base64, session id, CSRF hash) carries
        // digits.
        [/\b(?=[A-Za-z0-9+=_-]*[0-9])[A-Za-z0-9+=_-]{24,}\b/g, REDACTED],
    ];

    const masked = rules.reduce(function (acc, rule) {
        return acc.replace(rule[0], rule[1]);
    }, text);

    // A run of digits long enough to be a phone or card number, counted AFTER separators are
    // removed. A pattern cannot do this alone, and getting it wrong matters: the first version
    // matched seven characters INCLUDING separators, which blanked '23-0500' - a part number, and
    // the single most valuable string in any catalog diagnostic.
    //
    // Nine digits is the threshold. A phone is ten, a card fifteen or sixteen; part numbers here
    // run six to eight with separators. That leaves a seven-digit local number surviving inside an
    // exception message - a deliberate trade, because part numbers appear in exception messages
    // constantly, local numbers essentially never, and `extra` (where a phone column would
    // actually land) is not sent at all.
    return masked.replace(/\b[0-9][0-9 .()+-]{5,}[0-9]\b/g, function (match) {
        return match.replace(/[^0-9]/g, '').length >= 9 ? REDACTED : match;
    });
}

/**
 * TYPE COERCION, and the third rule this file runs on.
 *
 * Every allowlisted field is coerced to the primitive type the SDK declares for it, and anything
 * else is DROPPED. Not passed through, not stringified - dropped.
 *
 * An earlier version guarded with `if (typeof x !== 'object') return x` and `typeof v === 'string'
 * ? redact(v) : v`, which returns the INPUT when the input is the wrong shape. A review found what
 * that means: a string entry in `exception.values`, a string `mechanism`, a string frame, and an
 * OBJECT-valued `filename` all reached the payload intact - none was a string so none was
 * redacted, none was an object so none was rebuilt.
 *
 * Returning the input is never right for an allowlist.
 */
function str(value) {
    return typeof value === 'string' ? redactByShape(value) : undefined;
}

function num(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function bool(value) {
    return typeof value === 'boolean' ? value : undefined;
}

function defined(value) {
    return value !== undefined;
}

// A plain data record: something whose fields can be read and rebuilt. Arrays are excluded
// deliberately - an array where the SDK declares an object is malformed, and rebuilding it field
// by field would silently produce an empty object rather than dropping it.
function isPlainRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// SdkInfo, rebuilt. It was being copied BY REFERENCE - the last reference copy other than
// sdkProcessingMetadata - so its nested `packages` and `integrations` arrays travelled untouched.
// The SDK populates this itself today, which is exactly the "nothing puts data there yet"
// reasoning this file has been wrong about repeatedly.
function allowedSdk(sdk) {
    if (!isPlainRecord(sdk)) {
        return undefined;
    }

    return {
        name: str(sdk.name),
        version: str(sdk.version),
        integrations: Array.isArray(sdk.integrations) ? sdk.integrations.map(str).filter(defined) : undefined,
        packages: Array.isArray(sdk.packages)
            ? sdk.packages.filter(isPlainRecord).map(function (pkg) {
                  return { name: str(pkg.name), version: str(pkg.version) };
              })
            : undefined,
    };
}

// Depth cap AND a seen-set. The cap alone was here before the allowlist rewrite and was lost in
// it; a probe then found that a cyclic object - `c.self = c`, which a captureError() caller can
// easily produce - threw RangeError: Maximum call stack size exceeded from inside beforeSend. A
// throw there is worse than a leak: it takes the event with it and can surface as an error inside
// the error reporter.
const MAX_DEPTH = 8;

// A distinct marker for "this value was dropped", as opposed to "this value was null".
//
// Returning undefined for a dropped value works for an object KEY - JSON.stringify omits it - but
// not for an array ELEMENT, where undefined serializes as null. A review found the result:
// [1, NaN, 3] arrived as [1, null, 3], so a dropped element became a null that reads as data. A
// sentinel lets the container drop the slot entirely while a genuine null still survives, which a
// bare undefined check could not distinguish.
const DROP = Symbol('drop');

// TRUNCATION IS NOT DROPPING, and conflating them cost the shallow half of a deep structure.
//
// With empty containers dropped for parity, returning DROP at the depth cap made the parent empty,
// which dropped IT, all the way to the root - a nine-level context disappeared completely rather
// than being cut off at eight. A non-empty marker keeps every level above the cap, and says in the
// UI that something was cut rather than implying nothing was there.
const TRUNCATED = '***TRUNCATED***';

function kept(value) {
    return value !== DROP;
}

function redactValues(value, depth, seen) {
    const d = depth || 0;

    if (typeof value === 'string') {
        return redactByShape(value);
    }

    if (value === null) {
        return null;
    }

    if (typeof value !== 'object') {
        // Only JSON-safe scalars survive. A function, a symbol or a bigint would break the
        // transport rather than travel, and has no diagnostic value here.
        //
        // NUMBERS GO THROUGH num(), which requires Number.isFinite. `typeof NaN === 'number'` and
        // so is Infinity, so a bare typeof check let both through and JSON.stringify turned them
        // into null - the field arrived as a lie rather than as a value.
        if (typeof value === 'boolean') {
            return value;
        }

        const n = num(value);

        return n === undefined ? DROP : n;
    }

    if (d >= MAX_DEPTH) {
        return TRUNCATED;
    }

    const visited = seen || new Set();
    if (visited.has(value)) {
        return DROP;
    }
    visited.add(value);

    if (Array.isArray(value)) {
        // Filtered, not mapped-to-undefined. An array cannot omit a slot the way an object omits
        // a key, so a dropped element would otherwise become a null in the payload.
        const list = value
            .map(function (item) {
                return redactValues(item, d + 1, visited);
            })
            .filter(kept);

        return list.length > 0 ? list : DROP;
    }

    const out = {};
    Object.keys(value).forEach(function (key) {
        const redacted = redactValues(value[key], d + 1, visited);
        if (kept(redacted)) {
            out[key] = redacted;
        }
    });

    // AN EMPTY CONTAINER IS DROPPED, and this is the one place the two halves cannot simply be
    // written the same way: PHP has no empty map. `[]` is both an empty list and an empty map
    // there, so a map whose every value was filtered encoded as `[]` while this half encoded `{}`
    // - a review caught exactly that divergence.
    //
    // Dropping either kind of empty container removes the difference instead of reconciling it,
    // and costs nothing: an empty container carries no diagnostic value, and the parent has
    // already recorded that the key existed by dropping it.
    return Object.keys(out).length > 0 ? out : DROP;
}

// Tag values are COERCED to redacted strings and anything else is dropped, mirroring
// PaceSentry::stringValuesOnly(). Sentry declares tags as string-valued; redactValues() alone left
// a nested object in place - rebuilt and redacted inside, but still an object where a string
// belongs, and still divergent from the PHP half, which drops it.
function stringValuesOnly(record) {
    const out = {};

    Object.keys(record).forEach(function (key) {
        const value = record[key];
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            out[key] = redactByShape(String(value));
        }
    });

    return out;
}

function urlWithoutQuery(url) {
    return typeof url === 'string' ? str(url.split(/[?#]/)[0]) : undefined;
}

/**
 * A breadcrumb keeps its shape - category, type, level, timing - and a shape-redacted message.
 * Its DATA is reduced to two structural fields. For a fetch breadcrumb the rest is the request
 * body and the full URL, which is exactly the free-form content this rewrite exists to stop
 * sending. What survives is enough to read the sequence of events, which is what a breadcrumb is
 * for.
 */
export function scrubBreadcrumb(breadcrumb) {
    // A non-record breadcrumb is DROPPED, not returned. The previous guard was `if (!breadcrumb)
    // return breadcrumb`, so a string breadcrumb fell through to property reads and produced an
    // object of undefineds - harmless by luck rather than by design.
    if (!isPlainRecord(breadcrumb)) {
        return undefined;
    }

    const allowed = {
        category: str(breadcrumb.category),
        type: str(breadcrumb.type),
        level: str(breadcrumb.level),
        message: str(breadcrumb.message),
        timestamp: num(breadcrumb.timestamp),
    };

    if (isPlainRecord(breadcrumb.data)) {
        const kept = {
            method: str(breadcrumb.data.method),
            status_code: num(breadcrumb.data.status_code),
        };

        if (kept.method !== undefined || kept.status_code !== undefined) {
            allowed.data = kept;
        }
    }

    return allowed;
}

/**
 * A stack FRAME, rebuilt. This was the last place the allowlist copied a nested structure by
 * reference, and a review found it: StackFrame carries `vars`, `context_line`, `pre_context`,
 * `post_context` and `module_metadata` (typed `any`), none of which was being looked at.
 *
 * Dropped, and why each:
 *   vars            local variables at the throw site - the highest-risk field in any payload
 *   context_line    a line of source. Minified in production, so near-useless here, and a
 *   pre_context     minified line is one long string that can carry an inlined literal
 *   post_context
 *   module_metadata typed `any`; there is no way to reason about what a caller puts in it
 *   instruction_addr / addr_mode  native-only, never populated by this SDK
 *
 * Kept: everything that locates the frame, plus debug_id, the opaque link to an uploaded source
 * map. Dropping that would silently break symbolication if source-map upload is ever turned on,
 * and it carries no user data.
 */
function allowedFrame(frame) {
    return {
        filename: urlWithoutQuery(frame.filename),
        abs_path: urlWithoutQuery(frame.abs_path),
        function: str(frame.function),
        module: str(frame.module),
        platform: str(frame.platform),
        lineno: num(frame.lineno),
        colno: num(frame.colno),
        in_app: bool(frame.in_app),
        debug_id: str(frame.debug_id),
    };
}

/**
 * The MECHANISM, rebuilt. Its `data` is documented by the SDK as "arbitrary data to be associated
 * with the mechanism (for example, errors coming from event handlers include the handler name and
 * the event target)" - free-form by design, and shown in the UI directly above the stacktrace.
 * Everything else on it is structural.
 */
function allowedMechanism(mechanism) {
    if (!isPlainRecord(mechanism)) {
        return undefined;
    }

    return {
        type: str(mechanism.type),
        handled: bool(mechanism.handled),
        synthetic: bool(mechanism.synthetic),
        source: str(mechanism.source),
        is_exception_group: bool(mechanism.is_exception_group),
        exception_id: num(mechanism.exception_id),
        parent_id: num(mechanism.parent_id),
    };
}

function allowedFramesOmitted(range) {
    if (!Array.isArray(range) || range.length !== 2) {
        return undefined;
    }

    const pair = range.map(num);

    return pair.every(defined) ? pair : undefined;
}

function allowedException(value) {
    const out = {
        // `type` is NOT a safe class name. The browser SDK derives it from `ex.name`
        // (eventbuilder.js extractType), so `throw { name: 'buyer@example.com' }` puts an address
        // here - which is exactly what a review found after this file assumed otherwise.
        type: str(value.type),
        // A non-Error throw - `throw { password: 'x' }` - leaves an object here, and an object is
        // exactly what this rewrite stopped sending. str() drops it.
        value: str(value.value),
        mechanism: allowedMechanism(value.mechanism),
    };

    const frames = value.stacktrace && value.stacktrace.frames;
    if (Array.isArray(frames)) {
        out.stacktrace = {
            // Non-record entries are filtered out, not forwarded. A string in this array used to
            // be returned untouched.
            frames: frames.filter(isPlainRecord).map(allowedFrame),
            // Sentry declares this as exactly two integers. Mapping through num() alone left a
            // malformed entry as [null, 2]; the whole field is dropped unless it is well formed.
            frames_omitted: allowedFramesOmitted(value.stacktrace.frames_omitted),
        };
    }

    return out;
}

/**
 * The event, rebuilt. Nothing is deleted from the incoming object - a NEW one is returned holding
 * only what is named here. That inversion is the design: editing means every field ships unless
 * something removed it, and nine rounds proved that "something" is never complete. Building means
 * every field is dropped unless something copied it.
 */
export function scrubEvent(event) {
    // A malformed event is DROPPED - beforeSend returning undefined discards it - rather than
    // forwarded unchanged. Returning the input is never right for an allowlist.
    if (!isPlainRecord(event)) {
        return undefined;
    }

    const allowed = {
        event_id: str(event.event_id),
        timestamp: num(event.timestamp),
        platform: str(event.platform),
        level: str(event.level),
        logger: str(event.logger),
        release: str(event.release),
        environment: str(event.environment),
        sdk: allowedSdk(event.sdk),
        type: str(event.type),
        // The SDK reads live Scope objects back out of this AFTER beforeSend (core client.js) and
        // drops it before transmission, so it is carried by reference rather than rebuilt.
        sdkProcessingMetadata: event.sdkProcessingMetadata,
    };

    allowed.message = str(event.message);
    allowed.transaction = str(event.transaction);

    if (isPlainRecord(event.logentry)) {
        allowed.logentry = { message: str(event.logentry.message) };
    }

    if (Array.isArray(event.fingerprint)) {
        allowed.fingerprint = event.fingerprint.map(str).filter(function (part) {
            return part !== undefined;
        });
    }

    // Our own tags, set by initSentry. Values are still shape-redacted: a tag is a place a future
    // caller will put something, and not relying on what callers do today is the point.
    if (isPlainRecord(event.tags)) {
        allowed.tags = stringValuesOnly(event.tags);
    }

    // The id ONLY. email, ip_address and username are simply not copied.
    // Only a string or a number id. An object id would previously have become '[object Object]'.
    if (isPlainRecord(event.user) && str(String(event.user.id)) !== undefined) {
        const id = event.user.id;
        if (typeof id === 'string' || typeof id === 'number') {
            allowed.user = { id: str(String(id)) };
        }
    }

    if (isPlainRecord(event.contexts)) {
        const contexts = {};
        Object.keys(event.contexts).forEach(function (name) {
            if (ALLOWED_CONTEXTS.indexOf(name) !== -1) {
                const redacted = redactValues(event.contexts[name]);
                if (kept(redacted)) {
                    contexts[name] = redacted;
                }
            }
        });
        if (Object.keys(contexts).length > 0) {
            allowed.contexts = contexts;
        }
    }

    if (event.exception && Array.isArray(event.exception.values)) {
        allowed.exception = {
            // Non-record entries are filtered out. A STRING in this array used to be returned
            // untouched, because it was neither an object to rebuild nor a string field to redact.
            values: event.exception.values.filter(isPlainRecord).map(allowedException),
        };
    }

    if (Array.isArray(event.breadcrumbs)) {
        allowed.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb).filter(function (crumb) {
            return crumb !== undefined;
        });
    }

    // The URL with its query string REMOVED, and nothing else from the request. Headers carried
    // the Meilisearch Authorization value and the Referer's copy of ?au=; there is no data
    // property worth the risk.
    if (isPlainRecord(event.request) && typeof event.request.url === 'string') {
        allowed.request = {
            url: urlWithoutQuery(event.request.url),
            method: str(event.request.method),
        };
    }

    return allowed;
}

export function captureError(error, context) {
    if (context) {
        Sentry.withScope(function (scope) {
            scope.setContext('pace', context);
            Sentry.captureException(error);
        });

        return;
    }

    Sentry.captureException(error);
}

initSentry();
